import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, AlertCircle, Copy, Users, Link as LinkIcon, Tag } from "lucide-react";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import {
  listUpcomingEvents,
  listPastEvents,
  deleteEvent,
  generateEventShareLink,
} from "@/lib/api/admin";
import type { StoreEvent } from "@/lib/api/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Spinner } from "@/components/Spinner";
import { StatusBadge } from "@/components/StatusBadge";
import { formatDateTime } from "@/lib/utils/date";
import { EventFormDialog } from "../dialogs/EventFormDialog";
import { GuestlistDialog } from "../dialogs/GuestlistDialog";
import { ManageEventCategoriesDialog } from "../dialogs/ManageEventCategoriesDialog";

type EventsTabProps = {
  storeId: string;
  priceLimitBypass: boolean;
  storeCategory: string;
  storeSlug?: string;
};

const PAGE_SIZE = 20;

// Dedupes by id (defense-in-depth on top of the backend's own id tiebreaker) while preserving fetch order.
function dedupeById(events: StoreEvent[]): StoreEvent[] {
  return Array.from(new Map(events.map((e) => [e.id, e])).values());
}

// A bucket's own accumulated cursor-paginated list — used identically for "upcoming" and "past".
function useEventBucket(
  storeId: string,
  bucket: "upcoming" | "past",
  fetchPage: (limit: number, cursor?: string) => ReturnType<typeof listUpcomingEvents>,
  enabled: boolean
) {
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [events, setEvents] = useState<StoreEvent[]>([]);
  const [hasMore, setHasMore] = useState(true);

  const query = useQuery({
    queryKey: ["events", storeId, bucket, cursor],
    queryFn: () => fetchPage(PAGE_SIZE, cursor),
    enabled,
  });

  useEffect(() => {
    if (!query.data) return;
    setEvents((prev) => dedupeById(cursor ? [...prev, ...query.data.data] : query.data.data));
    setHasMore(query.data.has_more);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-sync when this page's response changes
  }, [query.data]);

  function loadMore() {
    if (query.data?.next_cursor) setCursor(query.data.next_cursor);
  }

  return {
    events,
    setEvents,
    hasMore,
    loadMore,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
  };
}

// Removes id from bucket, then — only when belongsHere — inserts event and re-sorts by starts_at.
function upsertInBucket(
  bucket: StoreEvent[],
  event: StoreEvent,
  belongsHere: boolean,
  ascending: boolean
): StoreEvent[] {
  const filtered = bucket.filter((e) => e.id !== event.id);
  if (!belongsHere) return filtered;
  const next = [...filtered, event];
  next.sort((a, b) => {
    const diff = new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime();
    return ascending ? diff : -diff;
  });
  return next;
}

// ─── Main EventsTab ───────────────────────────────────────────────────────────

export function EventsTab({ storeId, priceLimitBypass, storeCategory, storeSlug }: EventsTabProps) {
  const user = useAdminAuth();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [editingEvent, setEditingEvent] = useState<StoreEvent | undefined>();
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [defaultUnlisted, setDefaultUnlisted] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<StoreEvent | null>(null);
  const [registrationsEvent, setRegistrationsEvent] = useState<StoreEvent | null>(null);
  const [manageCategoriesOpen, setManageCategoriesOpen] = useState(false);

  const upcoming = useEventBucket(
    storeId,
    "upcoming",
    (limit, cursor) => listUpcomingEvents(user!, storeId, limit, cursor),
    !!user
  );
  const past = useEventBucket(
    storeId,
    "past",
    (limit, cursor) => listPastEvents(user!, storeId, limit, cursor),
    !!user
  );

  const isLoading = upcoming.isLoading || past.isLoading;
  const isError = upcoming.isError || past.isError;
  const totalLoaded = upcoming.events.length + past.events.length;

  // No refetch — places the saved event straight into the right bucket, visible regardless of fetch-order position.
  function upsertEvent(event: StoreEvent) {
    const isUpcoming = new Date(event.ends_at) >= new Date();
    upcoming.setEvents((prev) => upsertInBucket(prev, event, isUpcoming, true));
    past.setEvents((prev) => upsertInBucket(prev, event, !isUpcoming, false));
  }

  function removeEvent(id: string) {
    upcoming.setEvents((prev) => prev.filter((e) => e.id !== id));
    past.setEvents((prev) => prev.filter((e) => e.id !== id));
  }

  const deleteMutation = useMutation({
    mutationFn: (e: StoreEvent) => deleteEvent(user!, storeId, e.id),
    onSuccess: (_, deleted) => {
      removeEvent(deleted.id);
      setDeleteTarget(null);
      toast.success("Event deleted.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const shareLinkMutation = useMutation({
    mutationFn: (e: StoreEvent) => generateEventShareLink(user!, storeId, e.id),
    onSuccess: async ({ invite_url }) => {
      await navigator.clipboard.writeText(invite_url);
      toast.success("Share link copied to clipboard.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const now = new Date();
  const isInitialLoad = isLoading && totalLoaded === 0;

  if (isInitialLoad) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="size-5 border-primary" />
      </div>
    );
  }

  if (isError && totalLoaded === 0)
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
        <AlertCircle className="size-6 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">Failed to load events.</p>
      </div>
    );

  function renderEvent(e: StoreEvent) {
    const isPast = new Date(e.ends_at) < now;
    return (
      <Card
        key={e.id}
        onClick={() => {
          setEditingEvent(e);
          setIsDuplicate(false);
          setFormKey((k) => k + 1);
          setDialogOpen(true);
        }}
        title={isPast ? "View event (ended — read only)" : "Edit event"}
        className="cursor-pointer hover:border-primary/50"
      >
        <CardContent className="flex gap-3 p-3">
          {e.poster_url && (
            <img
              src={e.poster_url}
              alt={e.title}
              className="w-16 h-16 rounded object-cover shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-sm wrap-break-word">{e.title}</p>
                  {e.visibility === "unlisted" && <StatusBadge status="unlisted" />}
                </div>
                {e.subtitle && <p className="text-xs text-muted-foreground">{e.subtitle}</p>}
              </div>
              <div className="flex gap-1 shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 gap-1 text-xs"
                  title="Guestlist"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    setRegistrationsEvent(e);
                  }}
                >
                  <Users className="size-3.5" />
                  {e.guest_count}
                </Button>
                {e.visibility === "unlisted" && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    title="Get share link"
                    disabled={shareLinkMutation.isPending}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      shareLinkMutation.mutate(e);
                    }}
                  >
                    <LinkIcon className="size-3.5" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  title="Make Duplicate"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    setEditingEvent(e);
                    setIsDuplicate(true);
                    setFormKey((k) => k + 1);
                    setDialogOpen(true);
                  }}
                >
                  <Copy className="size-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 text-muted-foreground enabled:hover:text-destructive enabled:hover:bg-destructive/10"
                  title="Delete"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    setDeleteTarget(e);
                  }}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </div>
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              <span>{formatDateTime(e.starts_at)}</span>
              <span className="mx-1">–</span>
              <span>{formatDateTime(e.ends_at)}</span>
            </div>
            <p className="text-xs text-muted-foreground">{e.points} pts</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <h3 className="font-semibold text-sm">Events ({totalLoaded})</h3>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onClick={() => setManageCategoriesOpen(true)}>
            <Tag className="size-3.5 mr-1" /> Manage Categories
          </Button>
          <Button
            size="sm"
            className="bg-purple-600 text-white enabled:hover:bg-purple-700"
            onClick={() => {
              setEditingEvent(undefined);
              setIsDuplicate(false);
              setDefaultUnlisted(true);
              setFormKey((k) => k + 1);
              setDialogOpen(true);
            }}
          >
            <Plus className="size-3.5 mr-1" /> Add Unlisted Event
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setEditingEvent(undefined);
              setIsDuplicate(false);
              setDefaultUnlisted(false);
              setFormKey((k) => k + 1);
              setDialogOpen(true);
            }}
          >
            <Plus className="size-3.5 mr-1" /> Add Event
          </Button>
        </div>
      </div>

      {totalLoaded === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">No events yet.</p>
      ) : (
        <div className="space-y-4">
          {upcoming.events.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Upcoming
              </p>
              {upcoming.events.map(renderEvent)}
              {upcoming.hasMore && (
                <div className="flex justify-center pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={upcoming.loadMore}
                    disabled={upcoming.isFetching}
                  >
                    {upcoming.isFetching ? (
                      <>
                        <Spinner className="size-4 border-primary mr-2" />
                        Loading...
                      </>
                    ) : (
                      "Load more"
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
          {past.events.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Past
              </p>
              {past.events.map(renderEvent)}
              {past.hasMore && (
                <div className="flex justify-center pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={past.loadMore}
                    disabled={past.isFetching}
                  >
                    {past.isFetching ? (
                      <>
                        <Spinner className="size-4 border-primary mr-2" />
                        Loading...
                      </>
                    ) : (
                      "Load more"
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <EventFormDialog
        key={formKey}
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingEvent(undefined);
          setIsDuplicate(false);
          setDefaultUnlisted(false);
        }}
        onSaved={upsertEvent}
        storeId={storeId}
        priceLimitBypass={priceLimitBypass}
        storeCategory={storeCategory}
        event={editingEvent}
        isDuplicate={isDuplicate}
        defaultUnlisted={defaultUnlisted}
      />

      <GuestlistDialog
        open={!!registrationsEvent}
        onOpenChange={(open) => {
          if (!open) setRegistrationsEvent(null);
        }}
        storeId={storeId}
        event={registrationsEvent}
      />

      <ManageEventCategoriesDialog
        open={manageCategoriesOpen}
        onOpenChange={setManageCategoriesOpen}
        storeId={storeId}
        storeSlug={storeSlug}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => {
          if (!o) setDeleteTarget(null);
        }}
        title="Delete event"
        description={`Delete "${deleteTarget?.title}"? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        isPending={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget)}
      />
    </div>
  );
}
